import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, Share2 } from 'lucide-react';
import axios from 'axios';
import ShareButtons from './ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://shelovesthee.com';

// Format relative time (e.g., "2h", "1d")
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Large Featured Story Card
const LargeFeaturedStory = ({ article }) => {
  if (!article) return null;
  
  // Build the correct link URL
  let linkUrl = `/news/${article.article_id}`;
  if (article.redirect_url) {
    linkUrl = article.redirect_url;
  } else if (article.seo?.slug) {
    // Check for special slugs that have dedicated pages
    if (article.seo.slug === 'swac-tv-weekend-guide') {
      linkUrl = '/swac-tv';
    } else if (article.seo.slug === 'jsu-aamu-game-of-week-2026') {
      linkUrl = '/jsu-vs-aamu-2026';
    } else {
      linkUrl = `/interactive/${article.article_id}`;
    }
  }
  
  const imageUrl = article.featured_image || article.image_url || article.homepage?.homepage_image;
  const category = article.category || 'SWAC Football';
  const title = article.homepage?.homepage_headline || article.title;
  const publishDate = article.published_at || article.created_at;
  const shareUrl = `${SITE_URL}${linkUrl}`;
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <Link 
        to={linkUrl}
        className="block group"
        data-testid="large-featured-story"
      >
        {/* Large Image */}
        <div className="aspect-[16/9] relative overflow-hidden bg-gradient-to-br from-[#00204E] to-[#003366]">
          {imageUrl ? (
            <img 
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-white/50 text-lg font-bold">SheLovesThee</span>
            </div>
          )}
        </div>
        
        {/* Content Below Image */}
        <div className="p-4 pb-2">
          <span className="text-[11px] font-bold text-red-600 uppercase tracking-wide">{category}</span>
          <h2 className="text-[#00204E] font-bold text-lg sm:text-xl leading-tight mt-1 group-hover:text-red-600 transition-colors line-clamp-3">
            {title}
          </h2>
        </div>
      </Link>
      
      {/* Share Row */}
      <div className="px-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-1 text-gray-500 text-xs">
          <Clock className="w-3 h-3" />
          <span>{formatRelativeTime(publishDate)}</span>
        </div>
        <ShareButtons 
          title={title} 
          url={shareUrl}
          variant="compact"
          showLabels={false}
        />
      </div>
    </div>
  );
};

// Smaller Horizontal Story Card (below featured)
const SecondaryStoryCard = ({ article }) => {
  if (!article) return null;
  
  // Build the correct link URL
  let linkUrl = `/news/${article.article_id}`;
  if (article.redirect_url) {
    linkUrl = article.redirect_url;
  } else if (article.seo?.slug) {
    if (article.seo.slug === 'swac-tv-weekend-guide') {
      linkUrl = '/swac-tv';
    } else if (article.seo.slug === 'jsu-aamu-game-of-week-2026') {
      linkUrl = '/jsu-vs-aamu-2026';
    } else {
      linkUrl = `/interactive/${article.article_id}`;
    }
  }
  
  const imageUrl = article.featured_image || article.image_url;
  const title = article.title;
  const publishDate = article.published_at || article.created_at;
  
  return (
    <Link 
      to={linkUrl}
      className="flex gap-4 bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow group"
      data-testid="secondary-story-card"
    >
      {/* Thumbnail on Left */}
      <div className="w-28 h-20 sm:w-36 sm:h-24 flex-shrink-0 rounded overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        {imageUrl ? (
          <img 
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-gray-400 text-xs">No Image</span>
          </div>
        )}
      </div>
      
      {/* Content on Right */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <h3 className="text-[#00204E] font-semibold text-sm sm:text-base leading-snug group-hover:text-red-600 transition-colors line-clamp-2">
          {title}
        </h3>
        <div className="flex items-center gap-1 mt-2 text-gray-500 text-xs">
          <Clock className="w-3 h-3" />
          <span>{formatRelativeTime(publishDate)}</span>
          {article.author && <span className="ml-2">• {article.author}</span>}
        </div>
      </div>
    </Link>
  );
};

// Carousel Story Card
const CarouselStoryCard = ({ article }) => {
  if (!article) return null;
  
  // Build the correct link URL
  let linkUrl = `/news/${article.article_id}`;
  if (article.redirect_url) {
    linkUrl = article.redirect_url;
  } else if (article.seo?.slug) {
    if (article.seo.slug === 'swac-tv-weekend-guide') {
      linkUrl = '/swac-tv';
    } else if (article.seo.slug === 'jsu-aamu-game-of-week-2026') {
      linkUrl = '/jsu-vs-aamu-2026';
    } else {
      linkUrl = `/interactive/${article.article_id}`;
    }
  }
  
  const imageUrl = article.featured_image || article.image_url;
  const category = article.category || 'News';
  const title = article.title;
  
  return (
    <Link 
      to={linkUrl}
      className="block flex-shrink-0 w-[280px] sm:w-[320px] bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow group snap-start"
      data-testid="carousel-story-card"
    >
      {/* Thumbnail */}
      <div className="aspect-[16/10] relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        {imageUrl ? (
          <img 
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-gray-400 text-sm">No Image</span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-3">
        <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">{category}</span>
        <h3 className="text-[#00204E] font-semibold text-sm leading-snug mt-1 group-hover:text-red-600 transition-colors line-clamp-2">
          {title}
        </h3>
      </div>
    </Link>
  );
};

// Main ESPN-Style News Section Component
const ESPNNewsSection = () => {
  const [articles, setArticles] = useState([]);
  const [interactiveArticles, setInteractiveArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredStory, setFeaturedStory] = useState(null);
  const [secondaryStory, setSecondaryStory] = useState(null);
  const [carouselStories, setCarouselStories] = useState([]);
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Fetch articles
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        // Fetch regular articles
        const articlesRes = await axios.get(`${BACKEND_URL}/api/articles?limit=20`);
        const regularArticles = Array.isArray(articlesRes.data) 
          ? articlesRes.data 
          : articlesRes.data.articles || [];
        
        // Fetch interactive articles from CMS
        const interactiveRes = await axios.get(`${BACKEND_URL}/api/interactive-articles?status=published&limit=20`);
        const cmsArticles = interactiveRes.data?.articles || [];
        
        setArticles(regularArticles);
        setInteractiveArticles(cmsArticles);
        
        // Combine and deduplicate all articles
        const allArticles = deduplicateArticles([...cmsArticles, ...regularArticles]);
        
        // Check for admin-selected stories (from localStorage for now, can be moved to backend)
        const savedFeaturedId = localStorage.getItem('homepage_featured_story');
        const savedSecondaryId = localStorage.getItem('homepage_secondary_story');
        const savedCarouselOrder = JSON.parse(localStorage.getItem('homepage_carousel_order') || '[]');
        
        // Find featured story (admin-selected or newest)
        let featured = null;
        if (savedFeaturedId) {
          featured = allArticles.find(a => (a.article_id || a.id) === savedFeaturedId);
        }
        // Check for CMS-marked featured (prioritize by featured_position)
        if (!featured) {
          const featuredCms = cmsArticles
            .filter(a => a.homepage?.is_featured)
            .sort((a, b) => (a.homepage?.featured_position || 999) - (b.homepage?.featured_position || 999));
          featured = featuredCms[0];
        }
        // Default to newest article
        if (!featured && allArticles.length > 0) {
          featured = allArticles[0];
        }
        setFeaturedStory(featured);
        
        // Find secondary story
        let secondary = null;
        const remainingAfterFeatured = allArticles.filter(a => 
          (a.article_id || a.id) !== (featured?.article_id || featured?.id)
        );
        
        if (savedSecondaryId) {
          secondary = remainingAfterFeatured.find(a => (a.article_id || a.id) === savedSecondaryId);
        }
        if (!secondary && remainingAfterFeatured.length > 0) {
          secondary = remainingAfterFeatured[0];
        }
        setSecondaryStory(secondary);
        
        // Carousel stories (remaining, excluding featured and secondary)
        const usedIds = [
          featured?.article_id || featured?.id,
          secondary?.article_id || secondary?.id
        ].filter(Boolean);
        
        let carousel = remainingAfterFeatured.filter(a => 
          !usedIds.includes(a.article_id || a.id)
        );
        
        // Apply saved order if exists
        if (savedCarouselOrder.length > 0) {
          const orderedCarousel = [];
          savedCarouselOrder.forEach(id => {
            const article = carousel.find(a => (a.article_id || a.id) === id);
            if (article) orderedCarousel.push(article);
          });
          // Add any new articles not in saved order
          carousel.forEach(a => {
            if (!orderedCarousel.find(o => (o.article_id || o.id) === (a.article_id || a.id))) {
              orderedCarousel.push(a);
            }
          });
          carousel = orderedCarousel;
        }
        
        setCarouselStories(carousel.slice(0, 10));
        
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, []);

  // Deduplicate articles using stable identifiers (slug, redirect_url, or article_id)
  // This ensures we don't show the same article twice even if titles differ slightly
  const deduplicateArticles = (articles) => {
    const seen = new Map();
    const result = [];
    
    articles.forEach(article => {
      // Build a stable key from identifiers, in priority order
      const slug = article.seo?.slug || '';
      const redirectUrl = article.redirect_url || '';
      const articleId = article.article_id || article.id || '';
      
      // Create a composite key using available identifiers
      let stableKey = '';
      if (slug) {
        stableKey = `slug:${slug}`;
      } else if (redirectUrl) {
        stableKey = `redirect:${redirectUrl}`;
      } else if (articleId) {
        stableKey = `id:${articleId}`;
      } else {
        // Fallback to normalized title for articles without proper identifiers
        const title = (article.title || '').toLowerCase().trim()
          .replace(/[\u2013\u2014\-–—]/g, '')
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '')
          .slice(0, 50);
        stableKey = `title:${title}`;
      }
      
      if (!seen.has(stableKey)) {
        seen.set(stableKey, true);
        result.push(article);
      }
    });
    
    return result;
  };
  
  // List of article IDs/slugs to exclude from the feed (e.g., hardcoded elsewhere or duplicates)
  const EXCLUDED_ARTICLE_SLUGS = [
    // Ruffin article exists as both art_ and iart_ - exclude one
    'jsu-basketball-ruffin-2026'
  ];
  const EXCLUDED_REDIRECT_URLS = [
    '/jsu-basketball-2026-27' // If Ruffin article already shown via dedicated page link
  ];

  // Carousel scroll handlers
  const updateScrollButtons = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 340; // Card width + gap
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
      return () => carousel.removeEventListener('scroll', updateScrollButtons);
    }
  }, [carouselStories]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-gray-200 rounded-lg h-64 animate-pulse" />
        <div className="bg-gray-200 rounded-lg h-24 animate-pulse" />
        <div className="flex gap-4 overflow-hidden">
          {[1,2,3].map(i => (
            <div key={i} className="w-80 h-48 bg-gray-200 rounded-lg animate-pulse flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="espn-news-section">
      {/* 1. LARGE FEATURED STORY */}
      {featuredStory && (
        <LargeFeaturedStory article={featuredStory} />
      )}
      
      {/* 2. SMALLER STORY UNDERNEATH */}
      {secondaryStory && (
        <SecondaryStoryCard article={secondaryStory} />
      )}
      
      {/* 3. SWIPEABLE TOP STORIES CAROUSEL */}
      {carouselStories.length > 0 && (
        <div className="mt-6 min-w-0 max-w-full">
          {/* Header with Navigation Arrows */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[#00204E] font-bold text-sm uppercase tracking-wide">
              Top Stories
            </h3>
            
            {/* Desktop Navigation Arrows */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => scrollCarousel('left')}
                disabled={!canScrollLeft}
                className={`p-1.5 rounded-full border transition-colors ${
                  canScrollLeft 
                    ? 'border-gray-300 hover:bg-gray-100 text-gray-600' 
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
                data-testid="carousel-nav-left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollCarousel('right')}
                disabled={!canScrollRight}
                className={`p-1.5 rounded-full border transition-colors ${
                  canScrollRight 
                    ? 'border-gray-300 hover:bg-gray-100 text-gray-600' 
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
                data-testid="carousel-nav-right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Carousel Container */}
          <div 
            ref={carouselRef}
            className="flex min-w-0 max-w-full gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory scroll-smooth"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
            data-testid="stories-carousel"
          >
            {carouselStories.map((article, idx) => (
              <CarouselStoryCard 
                key={article.article_id || article.id || idx} 
                article={article} 
              />
            ))}
          </div>
        </div>
      )}
      
      {/* VIEW ALL NEWS Link */}
      <div className="pt-2">
        <Link 
          to="/news" 
          className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-semibold transition-colors"
          data-testid="view-all-news-link"
        >
          View All News <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default ESPNNewsSection;
